<template>
  <div class="signature-modal mp-modal-card">
    <template v-if="loaded">
      <!-- Create header -->
      <template v-if="createMode" ->
        <p class="has-text-weight-bold is-size-4 mb-2">
          {{ $t("bnpl:application.signature.modal.new.title") }}
        </p>

        <p class="mb-5 subtitle">
          {{ $t("bnpl:application.signature.modal.new.subtitle") }}
        </p>
      </template>

      <!-- Modify header -->
      <template v-else>
        <p class="has-text-weight-bold is-size-4 mb-5">
          {{ $t("bnpl:application.signature.modal.update.title") }} -
          {{ signature.id }}
        </p>

        <div>
          <div class="signature-modal-header-row">
            <div>
              <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                {{
                  $t("bnpl:application.signature.modal.update.header.status")
                }}
              </p>
              <span class="has-text-weight-medium">{{
                $t(SIGNATURE_STATUS.LABEL[signature.status])
              }}</span>
            </div>

            <div>
              <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                {{
                  $t("bnpl:application.signature.modal.update.header.signed")
                }}
              </p>
              <span class="has-text-weight-medium">{{
                signature.signed ? formatDateTime(signature.signed) : "-"
              }}</span>
            </div>

            <div>
              <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                {{
                  $t(
                    "bnpl:application.signature.modal.update.header.close_reason"
                  )
                }}
              </p>
              <span class="has-text-weight-medium">{{
                signature.closeReason
                  ? $t(SIGNATURE_CLOSE_REASON.LABEL[signature.closeReason])
                  : "-"
              }}</span>
            </div>
          </div>

          <div class="signature-modal-header-row">
            <div>
              <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                {{
                  $t("bnpl:application.signature.modal.update.header.created")
                }}
              </p>
              <span class="has-text-weight-medium">{{
                formatDateTime(signature.created)
              }}</span>
            </div>

            <div>
              <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                {{
                  $t("bnpl:application.signature.modal.update.header.due_date")
                }}
              </p>
              <span class="has-text-weight-medium">{{
                signature.dueDate ? formatDateTime(signature.dueDate) : "-"
              }}</span>
            </div>

            <div>
              <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                {{
                  $t(
                    "bnpl:application.signature.modal.update.header.created_by"
                  )
                }}
              </p>
              <span class="has-text-weight-medium">{{
                signature.createdBy
              }}</span>
            </div>
          </div>

          <div class="signature-modal-header-row">
            <div>
              <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                {{
                  $t("bnpl:application.signature.modal.update.header.active")
                }}
              </p>
              <span class="has-text-weight-medium">{{
                signature.active ? "Igen" : "Nem"
              }}</span>
            </div>
          </div>

          <div v-if="signature.userComment" class="mb-4">
            <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
              {{
                $t(
                  "bnpl:application.signature.modal.update.header.user_comment"
                )
              }}
            </p>
            <span class="has-text-weight-medium">{{
              signature.userComment
            }}</span>
          </div>

          <hr />

          <p class="has-text-weight-bold is-size-4 mb-3">
            {{ $t("bnpl:application.signature.modal.documents.subtitle") }}
          </p>
        </div>
      </template>

      <form @submit.prevent="save">
        <validation-observer ref="observer">
          <!-- Operator documents -->
          <template
            v-if="needOperatorDocuments && needOperatorDocuments.length > 0"
          >
            <p class="has-text-weight-bold is-size-5 mb-3">
              {{
                $t(
                  "bnpl:application.signature.modal.operator_documents.subtitle"
                )
              }}
            </p>

            <template v-if="createMode">
              <bnpl-application-signature-document-upload
                v-for="document in needOperatorDocuments"
                :key="document.identifier"
                :document="document"
                :createMode="createMode"
                ref="uploads"
                class="mb-2"
              />
            </template>
            <template v-else>
              <bnpl-application-signature-document
                v-for="document in signature.documents"
                :key="document.promptIdentifier"
                :document="document"
                :createMode="createMode"
                class="mb-2"
              />
            </template>
          </template>

          <!-- Fix documents -->
          <template v-if="fixedDocuments && fixedDocuments.length > 0">
            <p class="has-text-weight-bold is-size-5 mb-3 mt-5">
              {{
                $t("bnpl:application.signature.modal.fix_documents.subtitle")
              }}
            </p>

            <bnpl-application-signature-document
              v-for="document in fixedDocuments"
              :key="document.promptIdentifier"
              :createMode="createMode"
              :document="document"
              class="mb-2"
            />
          </template>

          <!-- Generated documents -->
          <template v-if="generatedDocuments && generatedDocuments.length > 0">
            <p class="has-text-weight-bold is-size-5 mb-3 mt-5">
              {{
                $t(
                  "bnpl:application.signature.modal.dynamic_documents.subtitle"
                )
              }}
            </p>

            <bnpl-application-signature-document
              v-for="document in generatedDocuments"
              :key="document.promptIdentifier"
              :document="document"
              :createMode="createMode"
              class="mb-2"
            />
          </template>

          <!-- Other settings documents -->
          <template v-if="createMode && isDueDateRequired">
            <p class="has-text-weight-bold is-size-5 mb-5 mt-5">
              {{ $t("bnpl:application.signature.modal.other.subtitle") }}
            </p>

            <ValidationProvider
              name="dueDate"
              :rules="'required'"
              v-slot="{ errors, valid }"
            >
              <b-field
                :label="
                  $t('bnpl:application.signature.modal.due_date.input.label')
                "
                :type="{ 'is-danger': errors[0], 'is-success': valid }"
                :message="errors"
                label-position="on-border"
                class="validator-field mb-0"
              >
                <b-datetimepicker
                  class="due-date-picker"
                  v-model="dueDate"
                  :icon-right="dueDate ? 'close-circle' : ''"
                  icon-right-clickable
                  @icon-right-click="clearDateTime"
                  position="is-top-left"
                  :min-datetime="new Date()"
                >
                </b-datetimepicker>
              </b-field>
            </ValidationProvider>
          </template>

          <template v-if="createMode || canWithdraw">
            <b-field grouped class="mt-6">
              <b-button
                v-if="createMode"
                type="is-blue"
                :value="$t('bnpl:application.signature.modal.button.save')"
                tag="input"
                @click="save"
              />
              <b-button
                v-else
                type="is-red"
                :value="$t('bnpl:application.signature.modal.button.withdraw')"
                tag="input"
                @click="withdraw"
              />

              <b-button type="is-text" class="ml-4" @click="cancelModal"
                >{{ $t("bnpl:application.signature.modal.button.cancel") }}
              </b-button>
            </b-field>
          </template>
        </validation-observer>
      </form>

      <div class="close-btn cursor-pointer" @click="cancelModal">
        <b-icon icon-pack="fas" icon="times" type="is-grey-icon" />
      </div>
    </template>

    <div v-else class="column">
      <b-skeleton width="30%" :animated="true"></b-skeleton>
      <b-skeleton width="70%" :animated="true"></b-skeleton>
      <b-skeleton width="20%" :animated="true"></b-skeleton>
      <b-skeleton width="60%" :animated="true"></b-skeleton>
      <b-skeleton width="40%" :animated="true"></b-skeleton>
      <b-skeleton width="50%" :animated="true"></b-skeleton>
    </div>
  </div>
</template>

<script>
import i18n from "@/plugins/i18n";
import BnplApplicationSignatureDocument from "./BnplApplicationSignatureDocument";
import BnplApplicationSignatureDocumentUpload from "./BnplApplicationSignatureDocumentUpload";
import {
  BNPL_APPLICATION_STATUS,
  SIGNATURE_CLOSE_REASON,
  SIGNATURE_STATUS,
} from "@/utils/const";
import { formatDateTime } from "@/utils/util";

export default {
  name: "BnplApplicationSignatureModal",
  components: {
    BnplApplicationSignatureDocumentUpload,
    BnplApplicationSignatureDocument,
  },
  props: {
    documents: {
      type: Array,
      required: true,
    },
    offerId: {
      type: Number,
      required: true,
    },
    bnplApplicationId: {
      type: Number,
      required: true,
    },
    signatureId: {
      type: Number,
      required: false,
    },
    bnplApplicationStatus: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isModalOpen: false,
      isDueDateRequired: false,
      dueDate: null,
      generatedDocuments: [],
      fixedDocuments: [],
      needOperatorDocuments: [],
      createMode: true,
      signature: {},
      loaded: false,
      SIGNATURE_STATUS,
      SIGNATURE_CLOSE_REASON,
      formatDateTime,
    };
  },
  async mounted() {
    if (this.signatureId) {
      this.createMode = false;

      await this.$store.dispatch(
        "bnplApplicationSignatureStore/fetch",
        this.signatureId
      );
      this.signature = this.$store.getters["bnplApplicationSignatureStore/get"](
        this.signatureId
      );
    } else {
      this.createMode = true;
      await this.$store.dispatch("offer/fetchOne", this.offerId);
      let offer = this.$store.getters["offer/get"](this.offerId);
      this.isDueDateRequired = offer.signatureDueDateRequired;
    }

    let signing = this.documents.filter((d) => d.type === "SIGNING");
    this.generatedDocuments = signing.filter((d) => d.origin === "GENERATED");
    this.fixedDocuments = signing.filter((d) => d.origin === "FIX");

    await this.$store.dispatch(
      "offerDocGeneration/fetchList",
      this.signature.applicationId
    );
    let offerDocGenerations = this.$store.getters["offerDocGeneration/list"];

    this.needOperatorDocuments = offerDocGenerations.filter(
      (d) => d.usage === "BNPL_APPLICATION" && d.type === "UPLOAD_BY_OPERATOR"
    );

    this.loaded = true;
  },
  computed: {
    canWithdraw() {
      return (
        !this.createMode &&
        (this.signature.status === SIGNATURE_STATUS.ENUM.WAITING ||
          this.signature.status === SIGNATURE_STATUS.ENUM.ACCEPTED ||
          this.signature.status === SIGNATURE_STATUS.ENUM.IN_PROGRESS ||
          (this.signature.status === SIGNATURE_STATUS.ENUM.SIGNED &&
            this.loanApplicationStatus ===
              BNPL_APPLICATION_STATUS.UNDER_REVIEW))
      );
    },
  },
  methods: {
    cancelModal() {
      this.$parent.close();
    },
    clearDateTime() {
      this.dueDate = null;
    },
    async withdraw() {
      if (!this.canWithdraw) {
        return;
      }

      let message = "bnpl:application.signature.modal.withdraw_confirm.message";
      if (
        this.signature.status === SIGNATURE_STATUS.ENUM.IN_PROGRESS ||
        this.signature.status === SIGNATURE_STATUS.ENUM.SIGNED
      ) {
        message =
          "bnpl:application.signature.modal.withdraw_confirm_extra_status.message";
      }

      this.$buefy.dialog.confirm({
        title: i18n.t(
          "bnpl:application.signature.modal.withdraw_confirm.title"
        ),
        message: i18n.t(message),
        cancelText: i18n.t(
          "bnpl:application.signature.modal.withdraw_confirm.button.cancel"
        ),
        confirmText: i18n.t(
          "bnpl:application.signature.modal.withdraw_confirm.button.confirm"
        ),
        type: "is-danger",
        onConfirm: async () => {
          await this.post(
            async () =>
              await this.$store.dispatch(
                "bnplApplicationSignatureStore/withdraw",
                this.signature.id
              )
          );
        },
      });
    },
    async save() {
      if (!this.createMode) {
        return;
      }

      const valid = await this.$refs.observer.validate();

      if (!valid) return;

      let files = [];
      let documents = [];

      let uploadComponents = this.$refs.uploads;
      if (uploadComponents) {
        uploadComponents
          .filter((upload) => !!upload.file)
          .forEach((upload) => {
            files.push(upload.file);
            documents.push({
              promptIdentifier: upload.document.identifier,
              promptId: upload.document.id,
              origin: "OPERATOR",
              type: "SIGNING",
            });
          });

        if (files.length !== uploadComponents.length) {
          return;
        }
      }

      const formData = new FormData();
      if (this.dueDate) {
        formData.append("dueDate", this.dueDate.toISOString());
      }
      if (files) {
        files.forEach((file) => formData.append("files", file));
        formData.append("documents", JSON.stringify(documents));
      }

      await this.post(
        async () =>
          await this.$store.dispatch("bnplApplicationSignatureStore/create", {
            id: this.bnplApplicationId,
            data: formData,
          })
      );
    },
    async post(method) {
      try {
        await method();
        this.$buefy.toast.open({
          message: i18n.t(
            "bnpl:application.signature.modal.message.save_successful"
          ),
          type: "is-success",
        });
        this.cancelModal();
        this.$emit("save");
      } catch (ex) {
        console.error(ex);
        this.$buefy.toast.open({
          message: i18n.t(
            "bnpl:application.signature.modal.message.save_failed"
          ),
          type: "is-danger",
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../assets/scss/colors";

.subtitle {
  color: $grey-icon;
}
</style>

<style lang="scss">
.signature-modal-header-row {
  display: flex;
  margin-bottom: 1rem;
  overflow-y: auto;
  max-height: 90vh;

  & > div {
    flex-shrink: 0;
    position: relative;
    width: 33%;
  }
}

.signature-modal.mp-modal-card {
  width: 768px;

  .documents {
    max-height: 20rem !important;
    overflow-y: auto;
  }
}
</style>
